/** The view over admin_audit. Reads only: this module has no write of any kind. */

export type AuditRow = {
  readonly auditId: string;
  readonly at: number;
  readonly adminUserId: string;
  readonly adminEmail: string;
  readonly action: string;
  readonly subjectUserId: string | null;
  readonly subjectEmail: string | null;
  readonly subjectName: string | null;
  readonly detail: Readonly<Record<string, unknown>>;
};

export type AuditTotals = {
  readonly entries: number;
  readonly identifying: number;
  readonly subjects: number;
  readonly admins: number;
  readonly lastDay: number;
  readonly firstAt: number | null;
  readonly lastAt: number | null;
};

export type ActionTally = {
  readonly action: string;
  readonly count: number;
  readonly lastAt: number;
};

export type AdminTally = {
  readonly adminEmail: string;
  readonly count: number;
  readonly subjects: number;
  readonly lastAt: number;
};

export type SubjectTally = {
  readonly subjectUserId: string;
  readonly subjectEmail: string | null;
  readonly subjectName: string | null;
  readonly count: number;
  readonly debriefReads: number;
  readonly lastAt: number;
};

export type Ledger = {
  readonly ok: true;
  readonly viewer: string;
  readonly page: number;
  readonly pages: number;
  readonly pageSize: number;
  readonly exportCap: number;
  readonly whole: boolean;
  readonly total: number;
  readonly filter: {
    readonly action: string;
    readonly admin: string;
    readonly subject: string;
  };
  readonly totals: AuditTotals;
  readonly rows: readonly AuditRow[];
  readonly actions: readonly ActionTally[];
  readonly admins: readonly AdminTally[];
  readonly subjects: readonly SubjectTally[];
};

export type LedgerFilter = {
  readonly action: string;
  readonly admin: string;
  readonly subject: string;
  readonly page: number;
};

export const EMPTY_FILTER: LedgerFilter = { action: "", admin: "", subject: "", page: 1 };

function ledgerParams(filter: LedgerFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.action.length > 0) params.set("action", filter.action);
  if (filter.admin.length > 0) params.set("admin", filter.admin);
  if (filter.subject.length > 0) params.set("subject", filter.subject);
  return params;
}

export function ledgerPath(filter: LedgerFilter): string {
  const params = ledgerParams(filter);
  if (filter.page > 1) params.set("page", String(filter.page));
  const tail = params.toString();
  return tail.length === 0 ? "/api/audit" : `/api/audit?${tail}`;
}

/** The whole filtered ledger rather than one page of it. Still a plain read. */
export function ledgerExportPath(filter: LedgerFilter): string {
  const params = ledgerParams(filter);
  params.set("export", "csv");
  return `/api/audit?${params.toString()}`;
}

export async function getLedger(filter: LedgerFilter, whole = false): Promise<Ledger> {
  let response: Response;
  try {
    const path = whole ? ledgerExportPath(filter) : ledgerPath(filter);
    response = await fetch(path, { credentials: "same-origin" });
  } catch {
    throw new Error("unreachable");
  }
  const parsed = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
  if (response.status === 403) throw new Error("refused");
  if (!response.ok || parsed === null || parsed.ok === false) {
    throw new Error(parsed?.error ?? `http_${response.status}`);
  }
  return parsed as Ledger;
}

const ACTION_LABEL: Record<string, string> = {
  "candidate.open": "Opened a candidate",
  "debrief.read": "Read a debrief",
  "observability.reading_failures.read": "Opened a reading failure",
  pass_granted: "Granted a pass",
  pass_extended: "Extended a pass",
  pass_cancelled: "Cancelled a pass",
  "roster.exported": "Exported this person in a roster file",
  payment_refunded: "Refunded a payment",
  mock_payment_captured: "Took a test payment",
  plan_created: "Created a plan",
  voucher_created: "Created a voucher",
};

const ACTION_MEANING: Record<string, string> = {
  "candidate.open":
    "An admin opened this person's record: their applications, rounds, mocks and competencies.",
  "debrief.read": "An admin read what this person wrote after a round, in full, as they wrote it.",
  "observability.reading_failures.read":
    "An admin looked at which of this person's debriefs the model could not read.",
  pass_granted: "An admin gave this person a pass rather than selling one.",
  pass_extended: "An admin pushed this person's pass expiry further out. No money moved.",
  pass_cancelled: "An admin ended this person's pass. No money moved: a refund is a separate act.",
  "roster.exported":
    "An admin downloaded the roster as a file, and this person's name, address and counts were in it.",
  payment_refunded: "An admin returned money this person had paid.",
  mock_payment_captured: "A payment was taken through the stand in gateway, not a real one.",
  plan_created: "An admin priced a plan. Names nobody.",
  voucher_created: "An admin made a discount code. Names nobody.",
};

function titled(raw: string): string {
  return raw
    .split(/[._\s]+/)
    .filter((one) => one.length > 0)
    .map((one) => `${(one[0] ?? "").toUpperCase()}${one.slice(1)}`)
    .join(" ");
}

export function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? titled(action);
}

export function actionMeaning(action: string): string {
  return ACTION_MEANING[action] ?? "An admin action, recorded when it happened.";
}

/** Whether the row names one person. The counterweight is only about these. */
export function identifying(row: AuditRow): boolean {
  return row.subjectUserId !== null;
}

export function subjectLabel(row: {
  readonly subjectUserId: string | null;
  readonly subjectEmail: string | null;
  readonly subjectName: string | null;
}): string {
  if (row.subjectUserId === null) return "Nobody in particular";
  if (row.subjectName !== null && row.subjectName.trim().length > 0) return row.subjectName.trim();
  if (row.subjectEmail !== null) return row.subjectEmail;
  return "Account since deleted";
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * What the row carried beside who and whose. Never the text of a debrief: the
 * ledger holds that one was read, and its length, and nothing it said.
 */
export function detailLine(row: AuditRow): string | null {
  const parts: string[] = [];
  const debriefs = row.detail["debriefs"];
  if (typeof debriefs === "number") parts.push(`${plural(debriefs, "debrief", "debriefs")} held`);
  const applications = row.detail["applications"];
  if (typeof applications === "number") {
    parts.push(`${plural(applications, "application", "applications")}`);
  }
  const mocks = row.detail["mocks"];
  if (typeof mocks === "number") parts.push(`${plural(mocks, "mock", "mocks")}`);
  const round = row.detail["round"];
  if (typeof round === "string" && round.length > 0) parts.push(round.replace(/_/g, " "));
  const characters = row.detail["characters"];
  if (typeof characters === "number") {
    parts.push(`${plural(characters, "character", "characters")}`);
  }
  return parts.length === 0 ? null : parts.join(", ");
}

export function clockOf(at: number): string {
  return new Date(at).toISOString().slice(11, 19);
}

export function dayOf(at: number): string {
  return new Date(at).toISOString().slice(0, 10);
}

export function stamp(at: number): string {
  return `${dayOf(at)} ${clockOf(at)}`;
}

export function agoLabel(at: number | null, now: number): string {
  if (at === null) return "never";
  const seconds = Math.max(0, Math.floor((now - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${plural(minutes, "minute", "minutes")} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, "hour", "hours")} ago`;
  return `${plural(Math.floor(hours / 24), "day", "days")} ago`;
}

/** The sentence under the title. It is the denominator for everything below it. */
export function ledgerSummary(totals: AuditTotals): string {
  if (totals.entries === 0) {
    return "Nothing has been recorded yet. A row is written here the moment an admin opens one named candidate or reads one debrief.";
  }
  return `${plural(totals.entries, "row", "rows")}, of which ${totals.identifying} named one person. ${plural(totals.subjects, "candidate has", "candidates have")} been opened, by ${plural(totals.admins, "admin", "admins")}.`;
}

export function spanLabel(totals: AuditTotals): string {
  if (totals.firstAt === null || totals.lastAt === null) return "No rows yet";
  if (totals.firstAt === totals.lastAt) return `One instant: ${stamp(totals.firstAt)}`;
  return `${dayOf(totals.firstAt)} to ${dayOf(totals.lastAt)}`;
}

export function filterSummary(filter: LedgerFilter, total: number): string {
  const parts: string[] = [];
  if (filter.action.length > 0) parts.push(actionLabel(filter.action).toLowerCase());
  if (filter.admin.length > 0) parts.push(`by ${filter.admin}`);
  if (filter.subject.length > 0) parts.push("about one candidate");
  if (parts.length === 0) return `${plural(total, "row", "rows")}, newest first`;
  return `${plural(total, "row", "rows")}: ${parts.join(", ")}`;
}

/**
 * What this ledger is, in the words that are true of it. Nothing signs a row
 * and nothing proves one was not altered, so it must never say otherwise.
 */
export const LEDGER_CLAIMS: readonly { readonly claim: string; readonly body: string }[] = [
  {
    claim: "Append only",
    body: "Rows are inserted and never updated or deleted. No endpoint in this console can change one, and there is no code path that tries.",
  },
  {
    claim: "Written before the data is served",
    body: "Opening a candidate writes the row first. If the write fails, the read is refused, so there is no way to see a dossier that left no trace.",
  },
  {
    claim: "It records that, not what",
    body: "A row names the admin, the action, the candidate and the instant. It never carries a line of what the candidate wrote, because a ledger holding the private text would defeat itself.",
  },
  {
    claim: "Not tamper evident",
    body: "These are ordinary rows in the same database as everything else. Anybody with write access to that database could alter one and nothing here would notice. A row carrying its own hash of the one before it would be the next step, and it has not been built.",
  },
];

export function refusalSentence(reason: string): string {
  const words: Record<string, string> = {
    refused: "Your session is not on the admin allowlist. Sign in again with an admin address.",
    unreachable: "The console could not reach the server. Nothing has been changed.",
  };
  return words[reason] ?? "The ledger did not load. Nothing has been changed.";
}
